import { Activity, CalendarClock, Euro, HouseIcon, TrendingUp } from 'lucide-react';
import { InspectionIcon } from '@/components/entity-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, useLocation, useNavigate, useParams } from 'react-router';

import { ROLE } from '@/common/role';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/use-permission';
import { cn } from '@/lib/utils';

export function AssetSidebar() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { assetId } = useParams<{ assetId: string }>();
	const isNew = assetId === 'new';
	const parentPath = `/assets/${assetId}`;
	const hasWorkPackageReadPermission = usePermission(ROLE.WORK_PACKAGES_READ);
	const hasSensorsReadPermission = usePermission(ROLE.SENSORS_READ);
	const hasInspectionManagementPagePermission = usePermission(ROLE.INSPECTION_MANAGEMENT_PAGE_VIEW);

	const { isHealthRoute, isTimePlanningRoute, isFinanceRoute, isLifespanRoute } = useMemo(() => {
		const isFinanceRoute = !!(matchRoutes([{ path: '/assets/:assetId/finance/*' }], location)?.length || 0);
		const isTimePlanningRoute = !!(matchRoutes([{ path: '/assets/:assetId/time-planning/*' }], location)?.length || 0);
		const isHealthRoute = !!(matchRoutes([{ path: '/assets/:assetId/health/*' }], location)?.length || 0);
		const isLifespanRoute = !!(matchRoutes([{ path: '/assets/:assetId/lifespan/*' }], location)?.length || 0);
		return {
			isTimePlanningRoute,
			isFinanceRoute,
			isHealthRoute,
			isLifespanRoute,
		};
	}, [location]);
	const tools = [
		{
			id: 'home',
			icon: HouseIcon,
			label: t('assets.nav.home'),
			// Back to the list with this asset still selected there
			onClick: () => navigate(isNew ? '/' : `/?selected=${assetId}`),
		},
		{
			id: 'inspections',
			icon: InspectionIcon,
			label: t('assets.nav.inspections'),
			onClick: () => navigate(parentPath),
			hidden: !hasInspectionManagementPagePermission,
		},
		{
			id: 'finance',
			icon: Euro,
			label: t('assets.nav.finance'),
			onClick: () => navigate(`${parentPath}/finance`),
			disabled: isNew,
			hidden: !hasWorkPackageReadPermission,
		},
		{
			id: 'time-planning',
			icon: CalendarClock,
			label: t('assets.nav.timePlanning'),
			onClick: () => navigate(`${parentPath}/time-planning`),
			disabled: isNew,
		},
		{
			id: 'health',
			icon: Activity,
			label: t('assets.nav.health'),
			onClick: () => navigate(`${parentPath}/health`),
			disabled: isNew,
			hidden: !hasSensorsReadPermission,
		},
		{
			id: 'lifespan',
			icon: TrendingUp,
			label: t('assets.nav.lifespan'),
			onClick: () => navigate(`${parentPath}/lifespan`),
			disabled: isNew,
		},
	].filter((tool) => !tool.hidden);
	const isAssetInspectionRoute = !isHealthRoute && !isTimePlanningRoute && !isFinanceRoute && !isLifespanRoute;
	return (
		<div className="hidden lg:flex w-16 bg-card border-r flex-col items-center py-4 gap-4 z-20 flex-none">
			{tools.map((tool) => {
				const isActive =
					(tool.id === 'inspections' && isAssetInspectionRoute) ||
					(tool.id === 'finance' && isFinanceRoute) ||
					(tool.id === 'time-planning' && isTimePlanningRoute) ||
					(tool.id === 'health' && isHealthRoute) ||
					(tool.id === 'lifespan' && isLifespanRoute);
				return (
					<Button
						key={tool.id}
						variant="ghost"
						size="icon-lg"
						className={cn(
							'rounded-xl h-10 w-10 transition-all duration-200 hover:cursor-pointer text-muted-foreground',
							isActive &&
								'bg-primary text-primary-foreground shadow-lg hover:bg-primary! hover:text-primary-foreground',
						)}
						title={tool.label}
						disabled={tool.disabled}
						onClick={tool.onClick}
					>
						<tool.icon className="size-5" />
					</Button>
				);
			})}
		</div>
	);
}
